// Schedule Wizard — assisted weekly schedule generator (MVP v2).
// Reads student.service_obligations + school.attendance_window from the DB
// (via /api/schedule/wizard/context). Per-run user can override duration/
// allow_grouping/monthly_minutes target, but obligations live on the student
// record — wizard never persists changes back to the student.
//
// Constraints honored: attendance pattern (AM/PM/Full Day),
// grade-based templates (lunch/recess/specials), cross-provider hard skip
// (with explicit "would conflict with X" reason in the unscheduled list),
// manual blocked_times the user adds.
//
// Output is a REVIEWABLE DRAFT only. The user explicitly accepts it into the
// Builder where it lands as unsaved blocks until they click Save.
import { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Sparkle,
  Plus,
  Trash,
  ArrowRight,
  ArrowLeft,
  Warning,
  CheckCircle,
  UsersThree,
  Sun,
  Moon,
  Calendar,
  Lightning,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { normalizeAttendancePattern } from "@/lib/studentProfileTaxonomy";
import { nativeOptions } from "@/lib/nativeOptions";
import { GRADE_COLORS, gradeColor, fmtTime, pct, TOTAL_MIN } from "./utils";
// FP3-072 — Contextual Help Center deep-links.
import HelpLink from "@/components/help/HelpLink";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri"];
const STEPS = [
  { key: "scope", label: "Scope" },
  { key: "needs", label: "Service Needs" },
  { key: "constraints", label: "Constraints" },
  { key: "review", label: "Review" },
];

function StepIndicator({ stepIdx }) {
  return (
    <div className="flex items-center gap-2 mb-3" data-testid="wizard-stepper">
      {STEPS.map((s, i) => (
        <div key={s.key} className="flex items-center gap-2">
          <div
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium ${
              i === stepIdx
                ? "bg-[#1E3F33] text-white"
                : i < stepIdx
                ? "bg-[#E8F0EB] text-[#1E3F33]"
                : "bg-[#F3F1EC] text-[#6B7280]"
            }`}
          >
            <span className="tabular-nums">{i + 1}</span>
            <span>{s.label}</span>
          </div>
          {i < STEPS.length - 1 && <span className="text-[#C9C7C1]">→</span>}
        </div>
      ))}
    </div>
  );
}

function AttendanceBadge({ pattern }) {
  // FP3-092 — canonical alias normalization (half_day_am → am etc.)
  // so legacy tokens badge identically to canonical ones.
  const pat = normalizeAttendancePattern(pattern);
  if (!pat || pat === "full_day") {
    return (
      <span className="inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-[#F3F1EC] text-[#4B5563]">
        <Calendar size={9} weight="bold" />
        Full Day
      </span>
    );
  }
  if (pat === "am") {
    return (
      <span className="inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-[#FDF8F3] text-[#D9A05B]">
        <Sun size={9} weight="bold" />
        AM
      </span>
    );
  }
  if (pat === "partial") {
    return (
      <span className="inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-[#F3F1EC] text-[#6B7280]">
        <Calendar size={9} weight="bold" />
        Partial
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-[#EEF2F7] text-[#5C7088]">
      <Moon size={9} weight="bold" />
      PM
    </span>
  );
}

function ObligationLabel({ ob }) {
  if (!ob) return <span className="text-[10px] text-[#9B9890] italic">No commitment</span>;
  // Iter 32 — read real values from the student-record service commitment
  // (kind / total_minutes / service_type / delivery.{sessions_per_period,
  // minutes_per_session}). Previously rendered "x/wk" because it expected
  // a `value` field that doesn't exist on the source object.
  const kind = ob.kind || "minutes_per_week";
  const total = ob.total_minutes != null ? ob.total_minutes : ob.value;
  const sessions = ob.delivery?.sessions_per_period;
  const sessionLen = ob.delivery?.minutes_per_session;
  const serviceType = ob.service_type;

  if (kind === "no_direct_service") {
    return (
      <span className="text-[10px] text-[#9B9890]">
        Consult · no direct
        {serviceType ? ` · ${serviceType}` : ""}
      </span>
    );
  }
  const periodLabel = kind === "minutes_per_month" || kind === "monthly_minutes" ? "mo" : "wk";
  const breakdown = sessions && sessionLen
    ? ` · ${sessions}× × ${sessionLen}m`
    : sessionLen
    ? ` · ${sessionLen}m sessions`
    : "";
  return (
    <span className="text-[10px] text-[#1F2937]">
      <span className="font-semibold tabular-nums">{total ?? "—"}</span>m/{periodLabel}
      {breakdown}
      {serviceType ? ` · ${serviceType}` : ""}
    </span>
  );
}

export default function ScheduleWizard({
  open,
  onOpenChange,
  provider,
  activePeriod,
  onAccept,
}) {
  const [stepIdx, setStepIdx] = useState(0);
  const [ctx, setCtx] = useState(null); // {school, grade_constraints, students, cross_provider_blocks}
  const [ctxLoading, setCtxLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  // Iter 33d (followup 2) — Lifted from ScopeStep so the visual chip
  // highlight survives Next/Back navigation. Empty set = no grade
  // filter (all students selected).
  const [gradeFilter, setGradeFilter] = useState(new Set());
  // Per-student per-run override: {duration_min?, monthly_minutes?, allow_grouping}
  // sessions_per_week/duration default to the student's obligation; user can override.
  const [overrides, setOverrides] = useState({});
  const [blockedTimes, setBlockedTimes] = useState([]);
  const [allowedDays, setAllowedDays] = useState([0, 1, 2, 3, 4]);
  const [workStart, setWorkStart] = useState("08:00");
  const [workEnd, setWorkEnd] = useState("15:00");
  const [applyGradeConstraints, setApplyGradeConstraints] = useState(true);
  const [applyAttendance, setApplyAttendance] = useState(true);
  const [applyCrossProvider, setApplyCrossProvider] = useState(true);
  const [applyFlexDays, setApplyFlexDays] = useState(true);
  const [applyProviderAvailability, setApplyProviderAvailability] = useState(true);
  const [coordinateWithExisting, setCoordinateWithExisting] = useState(true);
  // FP3-065 — Preferred group size (1-4). Defaults to 3 — wizard
  // groups compatible students up to this cap. group_size=1 forces
  // individual sessions. Surfaced in the constraints step + echoed
  // in the Review step header.
  const [groupSize, setGroupSize] = useState(3);
  // FP3-065 follow-up #6 improvement — Group-of-4 rescue toggle.
  // Default OFF so the wizard's behaviour is unchanged for users
  // who don't opt in. When ON, the wizard runs the main placement
  // loop with `groupSize` as the cap and, after the loop, attaches
  // under-scheduled students to compatible draft groups of 3 → 4.
  const [allowGroupOf4Rescue, setAllowGroupOf4Rescue] = useState(false);
  // FP3-065 follow-up #7 — Scheduling style preference. Default
  // "balanced" matches the legacy placement order. "compact"
  // consolidates the provider's footprint onto fewer days;
  // "spread_out" distributes across days AND picks within-day
  // slots that maximise the gap to other placements.
  const [schedulingStyle, setSchedulingStyle] = useState("balanced");
  const [disabledGradeConstraintIds, setDisabledGradeConstraintIds] = useState(new Set());
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  // Apply mode is asked at "Send to Builder" time, only if there's an existing schedule.
  const [applyChooserOpen, setApplyChooserOpen] = useState(false);
  const [applyMode, setApplyMode] = useState("coordinate");
  const [archivePrevious, setArchivePrevious] = useState(true);
  const [applying, setApplying] = useState(false);

  // Fetch context once when modal opens
  useEffect(() => {
    if (!open || !provider?.id) return;
    setCtxLoading(true);
    setStepIdx(0);
    setResult(null);
    api
      .getScheduleWizardContext({
        provider_id: provider.id,
        campus: provider.campus,
        schedule_period_id: activePeriod?.id,
      })
      .then((res) => {
        setCtx(res.data);
        // FP3-069 follow-up #9 — Default-selected set EXCLUDES
        // students who are already covered by a supervised
        // assistant. Pete shouldn't accidentally schedule
        // duplicate Pete-owned blocks for students Max is already
        // serving. The students remain in the list (visible +
        // labeled "Assigned to <Assistant>"); they're just
        // unchecked by default. User can opt in by clicking the
        // checkbox.
        const defaultIds = new Set(
          res.data.students
            .filter((s) => !s.assistant_covered)
            .map((s) => s.id)
        );
        setSelectedIds(defaultIds);
        // Default overrides: empty (use student record)
        const ov = {};
        res.data.students.forEach((s) => {
          ov[s.id] = { allow_grouping: true };
        });
        setOverrides(ov);
        setBlockedTimes([]);
        setDisabledGradeConstraintIds(new Set());
        // Reset grade filter for a fresh wizard run (state now lives at
        // the parent so it survives Next/Back, but should not bleed
        // across separate wizard sessions).
        setGradeFilter(new Set());
      })
      .catch((e) => {
        toast.error(`Could not load wizard context: ${e.message}`);
      })
      .finally(() => setCtxLoading(false));
  }, [open, provider]);

  // Memoize the empty-array fallback so a transient `ctx == null` render
  // doesn't hand a brand-new `[]` reference to every downstream useMemo /
  // useEffect each render. Without this, the `selectedStudents` useMemo
  // (and the gradeRulesCheck effect below) would recompute on every render
  // and trigger an infinite setState loop the moment the wizard opens.
  // See P0 navigation-trap fix (Jun 3, 2026).
  const allStudents = useMemo(() => ctx?.students || [], [ctx]);
  const selectedStudents = useMemo(
    () => allStudents.filter((s) => selectedIds.has(s.id)),
    [allStudents, selectedIds]
  );

  // Build a unique deduped list of grade-constraint templates relevant to the
  // selected students (so the UI shows "Gr 2 lunch", "Kinder specials", etc.
  // only when there's a selected student of that grade).
  const relevantGradeConstraints = useMemo(() => {
    if (!ctx?.grade_constraints) return [];
    const grades = new Set(selectedStudents.map((s) => s.grade));
    return ctx.grade_constraints.filter((c) => grades.has(c.grade));
  }, [ctx, selectedStudents]);

  // FP3-065 follow-up #7 audit — Grade Rules Check pre-flight.
  // Calls the backend resolver whenever the selected-student set
  // changes and surfaces one row per (school, grade) combination
  // touched by the run. Lets operators see at a glance whether
  // the wizard would actually load the grade rules they configured
  // (catches stale campus labels, missing school_id, int-grade
  // mismatches, etc.) BEFORE the run happens.
  const [gradeRulesCheck, setGradeRulesCheck] = useState({ rows: [], preflight_summary: null, loading: false });
  // Stable signature so identity-only changes to `selectedStudents` don't
  // re-fire this effect. Sorted comma-joined IDs are content-stable.
  const selectedIdsSignature = useMemo(
    () => selectedStudents.map((s) => s.id).sort().join(","),
    [selectedStudents]
  );
  useEffect(() => {
    let cancelled = false;
    if (!selectedIdsSignature) {
      // Don't re-emit a new {rows:[], loading:false} object on every render —
      // only set state if it actually differs. This is a belt-and-braces
      // guard against future regressions causing dep-instability above.
      setGradeRulesCheck((prev) =>
        prev.rows.length === 0 && !prev.loading ? prev : { rows: [], preflight_summary: null, loading: false }
      );
      return () => { cancelled = true; };
    }
    const ids = selectedIdsSignature.split(",");
    setGradeRulesCheck((prev) => (prev.loading ? prev : { ...prev, loading: true }));
    (async () => {
      try {
        const r = await api.wizardGradeRulesCheck(ids, provider?.id);
        if (!cancelled) setGradeRulesCheck({
          rows: r.data?.rows || [],
          preflight_summary: r.data?.preflight_summary || null,
          loading: false,
        });
      } catch {
        if (!cancelled) setGradeRulesCheck({ rows: [], preflight_summary: null, loading: false });
      }
    })();
    return () => { cancelled = true; };
  }, [selectedIdsSignature]);

  const toggleStudent = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const updateOverride = (id, field, value) =>
    setOverrides((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));

  const addBlockedTime = () =>
    setBlockedTimes((prev) => [
      ...prev,
      { day_of_week: -1, start_time: "12:00", end_time: "12:30", label: "", source: "manual" },
    ]);
  const updateBlocked = (idx, field, value) =>
    setBlockedTimes((prev) =>
      prev.map((b, i) => (i === idx ? { ...b, [field]: value } : b))
    );
  const removeBlocked = (idx) =>
    setBlockedTimes((prev) => prev.filter((_, i) => i !== idx));

  const toggleDay = (d) =>
    setAllowedDays((prev) =>
      prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort()
    );

  const toggleGradeConstraint = (id) =>
    setDisabledGradeConstraintIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const runWizard = async (overrideOpts = {}) => {
    if (!provider?.id || !activePeriod?.id) {
      toast.error("No active schedule period");
      return;
    }
    if (selectedStudents.length === 0) {
      toast.error("Select at least one student");
      return;
    }
    if (allowedDays.length === 0) {
      toast.error("Choose at least one allowed day");
      return;
    }
    setRunning(true);
    try {
      const payload = {
        provider_id: provider.id,
        schedule_period_id: activePeriod.id,
        students: selectedStudents.map((s) => {
          const ov = overrides[s.id] || {};
          return {
            student_id: s.id,
            allow_grouping: ov.allow_grouping ?? true,
            ...(ov.duration_min ? { duration_min: ov.duration_min } : {}),
            ...(ov.monthly_minutes ? { monthly_minutes: ov.monthly_minutes } : {}),
            ...(ov.sessions_per_week ? { sessions_per_week: ov.sessions_per_week } : {}),
          };
        }),
        blocked_times: blockedTimes,
        preferences: {
          allowed_days: allowedDays,
          work_start: workStart,
          work_end: workEnd,
          apply_grade_constraints: applyGradeConstraints,
          apply_attendance_window: applyAttendance,
          apply_cross_provider_conflicts: applyCrossProvider,
          apply_flex_days: applyFlexDays,
          coordinate_with_existing: coordinateWithExisting,
          apply_provider_availability: applyProviderAvailability,
          // FP3-065 — Forward the operator's chosen group-size cap.
          group_size: groupSize,
          // FP3-065 follow-up #6 improvement — Group-of-4 rescue.
          // `overrideOpts.allow_group_of_4_rescue` lets the Review
          // suggestion banner force-enable the toggle for a one-
          // click re-run without waiting for React state to flush.
          allow_group_of_4_rescue:
            overrideOpts.allow_group_of_4_rescue ?? allowGroupOf4Rescue,
          // FP3-065 follow-up #7 — Scheduling style.
          scheduling_style: overrideOpts.scheduling_style ?? schedulingStyle,
        },
        disabled_grade_constraint_ids: Array.from(disabledGradeConstraintIds),
      };
      const res = await api.generateScheduleDraft(payload);
      setResult(res.data);
      setStepIdx(3);
      toast.success(
        `Draft ready · ${res.data.summary.total_placed_blocks}/${res.data.summary.total_required_blocks} blocks placed`
      );
    } catch (e) {
      console.error(e);
      toast.error(`Wizard failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setRunning(false);
    }
  };

  const existingCount = ctx?.existing_schedule_block_count || 0;

  const acceptDraft = () => {
    if (!result?.proposed_blocks) return;
    if (existingCount > 0) {
      setApplyMode(coordinateWithExisting ? "coordinate" : "overwrite");
      setArchivePrevious(true);
      setApplyChooserOpen(true);
      return;
    }
    // No existing schedule — apply as coordinate (clean append).
    finalizeApply("coordinate", false);
  };

  const finalizeApply = async (mode, archive) => {
    if (!result?.proposed_blocks?.length) return;
    // Coordinate mode: ship drafts to Builder for review & Save (no DB writes here).
    if (mode === "coordinate") {
      onAccept(result.proposed_blocks, "coordinate");
      toast.success("Draft sent to Builder. Review and Save when ready.");
      setApplyChooserOpen(false);
      onOpenChange(false);
      return;
    }
    // Overwrite mode: archive existing + persist new in one transaction.
    // FP3-065 — Refuse client-side too. Backend has the same guard
    // but failing earlier surfaces a much clearer error than a 400.
    const placedBlocks = (result.proposed_blocks || []).filter(
      (b) => (b.block_kind || "service") === "service"
        && Array.isArray(b.student_ids)
        && b.student_ids.length > 0
    );
    if (placedBlocks.length === 0) {
      toast.error(
        "The wizard could not place any service blocks. Your current schedule was not changed.",
      );
      setApplyChooserOpen(false);
      return;
    }
    setApplying(true);
    try {
      const _ = archive; // archive is always on for now (safe default)
      const payload = {
        provider_id: provider.id,
        schedule_period_id: activePeriod.id,
        blocks: result.proposed_blocks,
        mode: "overwrite",
        archive_label: `Wizard run ${new Date().toLocaleDateString()}`,
      };
      const res = await api.applyWizardDraft(payload);
      toast.success(
        `Schedule replaced · ${res.data.inserted} new blocks · ${res.data.archived} archived`
      );
      // Tell parent to reload data (so Builder shows the freshly-saved schedule).
      onAccept([], "overwrite");
      setApplyChooserOpen(false);
      onOpenChange(false);
    } catch (e) {
      toast.error(`Apply failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setApplying(false);
    }
  };

  const next = () => setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));
  const prev = () => setStepIdx((i) => Math.max(i - 1, 0));

  const canNext = useMemo(() => {
    if (stepIdx === 0) return selectedIds.size > 0;
    if (stepIdx === 1) return selectedIds.size > 0;
    if (stepIdx === 2) return allowedDays.length > 0 && workEnd > workStart;
    return false;
  }, [stepIdx, selectedIds, allowedDays, workStart, workEnd]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="schedule-wizard"
        className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg heading-font">
            <Sparkle size={18} weight="duotone" className="text-[#1E3F33]" />
            Schedule Wizard
          </DialogTitle>
          <DialogDescription className="sr-only">
            Multi-step wizard that proposes a reviewable weekly schedule draft based on student service commitments, school constraints, and provider preferences.
          </DialogDescription>
          {ctx?.school && (
            <p className="text-[11px] text-[#6B7280]" data-testid="wizard-school-context">
              <span className="font-medium">{ctx.school.name}</span>
              <span className="mx-2 text-[#9B9890]">·</span>
              AM {ctx.school.attendance_window_am.start}–{ctx.school.attendance_window_am.end}
              {" · "}
              PM {ctx.school.attendance_window_pm.start}–{ctx.school.attendance_window_pm.end}
            </p>
          )}
        </DialogHeader>

        <StepIndicator stepIdx={stepIdx} />

        {/* Scrollable body — fixes the prior nested-scroll bug */}
        <div className="flex-1 overflow-y-auto pr-1 min-h-0" data-testid="wizard-body">
          {ctxLoading && (
            <div className="text-center py-8 text-xs text-[#6B7280]">Loading caseload…</div>
          )}
          {!ctxLoading && stepIdx === 0 && (
            <ScopeStep
              students={allStudents}
              selectedIds={selectedIds}
              toggleStudent={toggleStudent}
              setSelectedIds={setSelectedIds}
              gradeFilter={gradeFilter}
              setGradeFilter={setGradeFilter}
              preflightSummary={gradeRulesCheck.preflight_summary}
              preflightLoading={gradeRulesCheck.loading}
            />
          )}
          {!ctxLoading && stepIdx === 1 && (
            <NeedsStep
              students={selectedStudents}
              overrides={overrides}
              updateOverride={updateOverride}
            />
          )}
          {!ctxLoading && stepIdx === 2 && (
            <ConstraintsStep
              blockedTimes={blockedTimes}
              updateBlocked={updateBlocked}
              addBlockedTime={addBlockedTime}
              removeBlocked={removeBlocked}
              allowedDays={allowedDays}
              toggleDay={toggleDay}
              workStart={workStart}
              setWorkStart={setWorkStart}
              workEnd={workEnd}
              setWorkEnd={setWorkEnd}
              applyGradeConstraints={applyGradeConstraints}
              setApplyGradeConstraints={setApplyGradeConstraints}
              gradeRulesCheck={gradeRulesCheck}
              applyAttendance={applyAttendance}
              setApplyAttendance={setApplyAttendance}
              applyCrossProvider={applyCrossProvider}
              setApplyCrossProvider={setApplyCrossProvider}
              applyFlexDays={applyFlexDays}
              setApplyFlexDays={setApplyFlexDays}
              applyProviderAvailability={applyProviderAvailability}
              setApplyProviderAvailability={setApplyProviderAvailability}
              providerAvailability={ctx?.provider_availability}
              coordinateWithExisting={coordinateWithExisting}
              setCoordinateWithExisting={setCoordinateWithExisting}
              groupSize={groupSize}
              setGroupSize={setGroupSize}
              allowGroupOf4Rescue={allowGroupOf4Rescue}
              setAllowGroupOf4Rescue={setAllowGroupOf4Rescue}
              schedulingStyle={schedulingStyle}
              setSchedulingStyle={setSchedulingStyle}
              existingCount={existingCount}
              relevantGradeConstraints={relevantGradeConstraints}
              disabledGradeConstraintIds={disabledGradeConstraintIds}
              toggleGradeConstraint={toggleGradeConstraint}
              crossBlockCount={ctx?.cross_provider_blocks?.length || 0}
              flexDayCount={ctx?.flex_days?.length || 0}
            />
          )}
          {!ctxLoading && stepIdx === 3 && result && (
            <ReviewStep
              result={result}
              onApplyRescueSuggestion={async () => {
                // FP3-065 follow-up #6 improvement — One-click
                // opt-in from the Review suggestion banner. Flip
                // the toggle then re-run the wizard with all other
                // settings unchanged.
                setAllowGroupOf4Rescue(true);
                await runWizard({ allow_group_of_4_rescue: true });
              }}
              onSwitchStyle={async (nextStyle) => {
                // FP3-065 follow-up #7 — One-click style swap from
                // the compromise card. `overrideOpts.scheduling_style`
                // forces the requested style into the new run
                // without waiting for the React state to flush.
                setSchedulingStyle(nextStyle);
                await runWizard({ scheduling_style: nextStyle });
              }}
            />
          )}
        </div>

        <DialogFooter className="flex items-center justify-between gap-2 sm:justify-between mt-3 shrink-0">
          <div className="text-[11px] text-[#6B7280]">
            {stepIdx === 0 && `${selectedIds.size} student${selectedIds.size !== 1 ? "s" : ""} selected`}
            {stepIdx === 1 && `Service commitments from student record (override per-run)`}
            {stepIdx === 2 && `Constraints applied to draft`}
            {stepIdx === 3 && `Review proposal`}
          </div>
          <div className="flex items-center gap-2">
            {stepIdx > 0 && stepIdx < 3 && (
              <Button
                variant="outline"
                size="sm"
                onClick={prev}
                data-testid="wizard-back"
                className="rounded-lg h-8 text-xs"
              >
                <ArrowLeft size={12} className="mr-1" />
                Back
              </Button>
            )}
            {stepIdx < 2 && (
              <Button
                size="sm"
                onClick={next}
                disabled={!canNext}
                data-testid="wizard-next"
                className="bg-[#1E3F33] hover:bg-[#173127] text-white rounded-lg h-8 text-xs"
              >
                Next
                <ArrowRight size={12} className="ml-1" />
              </Button>
            )}
            {stepIdx === 2 && (
              <Button
                size="sm"
                onClick={runWizard}
                disabled={running || !canNext}
                data-testid="wizard-run"
                className="bg-[#1E3F33] hover:bg-[#173127] text-white rounded-lg h-8 text-xs"
              >
                <Sparkle size={12} className="mr-1" />
                {running ? "Generating..." : "Run wizard"}
              </Button>
            )}
            {stepIdx === 3 && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setStepIdx(2)}
                  data-testid="wizard-tweak"
                  className="rounded-lg h-8 text-xs"
                >
                  <ArrowLeft size={12} className="mr-1" />
                  Adjust inputs
                </Button>
                <Button
                  size="sm"
                  onClick={acceptDraft}
                  disabled={!result?.proposed_blocks?.length}
                  data-testid="wizard-accept"
                  title={
                    !result?.proposed_blocks?.length
                      ? "No blocks were placed — adjust inputs and re-run"
                      : "Send draft to Builder"
                  }
                  className="bg-[#1E3F33] hover:bg-[#173127] text-white rounded-lg h-8 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <CheckCircle size={12} weight="fill" className="mr-1" />
                  Send to Builder
                </Button>
              </>
            )}
          </div>
        </DialogFooter>

        {/* Apply chooser — shown when there's an existing schedule */}
        {applyChooserOpen && (
          <ApplyChooser
            existingCount={existingCount}
            applyMode={applyMode}
            setApplyMode={setApplyMode}
            archivePrevious={archivePrevious}
            setArchivePrevious={setArchivePrevious}
            applying={applying}
            onCancel={() => setApplyChooserOpen(false)}
            onConfirm={() => finalizeApply(applyMode, archivePrevious)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Step 1: Scope ─────────────────────────────────────────────────────────────
function ScopeStep({ students: rawStudents, selectedIds, toggleStudent, setSelectedIds, gradeFilter, setGradeFilter, preflightSummary, preflightLoading }) {
  // Iter 32 — Grade filter chips. Lets the provider build grade-by-grade
  // instead of scrolling a giant caseload.
  const allGrades = useMemo(() => {
    const set = new Set();
    rawStudents.forEach((s) => {
      if (s.grade != null && s.grade !== "") set.add(String(s.grade));
    });
    return Array.from(set).sort((a, b) => {
      // K first, then numeric
      // FP3-035B correction — Grades are all numeric ("0" = K).
      // String compare on numeric strings is wrong ("10" < "2") so
      // we parseInt for comparison.
      const ai = parseInt(a, 10);
      const bi = parseInt(b, 10);
      if (Number.isFinite(ai) && Number.isFinite(bi)) return ai - bi;
      return parseInt(a, 10) - parseInt(b, 10);
    });
  }, [rawStudents]);
  // Iter 33d (followup 2) — gradeFilter is now lifted to ScheduleWizard
  // so the chip highlight survives Next/Back navigation. ScopeStep
  // receives it via props. Empty set = no filter.

  // Iter 33d (followup) — Grade chips behave as a true filter that
  // overrides the wizard's default "everyone is selected" state. When at
  // least one grade chip is ON, selectedIds becomes EXACTLY the union
  // of students in those grades. Turning all chips off restores the
  // full caseload selection.
  //
  // FP3-069 follow-up #9 — Both branches EXCLUDE assistant-covered
  // students from the auto-selected set. They remain visible but
  // unchecked by default in either mode; users can still opt in
  // explicitly by clicking the checkbox.
  const toggleGrade = (g) => {
    setGradeFilter((prev) => {
      const next = new Set(prev);
      if (next.has(g)) next.delete(g);
      else next.add(g);
      if (next.size === 0) {
        // No grade filter → restore default: all NON-assistant-covered selected.
        setSelectedIds(new Set(
          rawStudents.filter((s) => !s.assistant_covered).map((s) => s.id)
        ));
      } else {
        const ids = rawStudents
          .filter((s) => next.has(String(s.grade)) && !s.assistant_covered)
          .map((s) => s.id);
        setSelectedIds(new Set(ids));
      }
      return next;
    });
  };
  const clearGradeFilter = () => setGradeFilter(new Set());

  // Alphabetize by last name then first name so the Scope list is predictable
  // regardless of seed/insert order.
  const filteredStudents = useMemo(() => {
    const list = gradeFilter.size === 0
      ? rawStudents
      : rawStudents.filter((s) => gradeFilter.has(String(s.grade)));
    return [...list].sort((a, b) => {
      const la = (a.last_name || "").localeCompare(b.last_name || "");
      if (la !== 0) return la;
      return (a.first_name || "").localeCompare(b.first_name || "");
    });
  }, [rawStudents, gradeFilter]);
  const students = filteredStudents;

  const allSelected = students.length > 0 && students.every((s) => selectedIds.has(s.id));
  const toggleAll = () => {
    if (allSelected) {
      // Deselect just the visible (filtered) students; preserve other selections
      setSelectedIds((prev) => {
        const next = new Set(prev);
        students.forEach((s) => next.delete(s.id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        students.forEach((s) => next.add(s.id));
        return next;
      });
    }
  };
  return (
    <div data-testid="wizard-step-scope" className="space-y-3">
      <p className="text-xs text-[#6B7280]">
        Pick the students you want included in this draft. Defaults to your full caseload. Filter by grade to build grade-by-grade.
      </p>
      {allGrades.length > 0 && (
        <div
          data-testid="wizard-grade-filter"
          className="flex items-center gap-1.5 flex-wrap"
        >
          <span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold mr-1">Grades:</span>
          <button
            data-testid="wizard-grade-filter-all"
            onClick={clearGradeFilter}
            className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${
              gradeFilter.size === 0
                ? "bg-[#1E3F33] text-white border-[#1E3F33]"
                : "bg-white text-[#6B7280] border-[#d4d2cc] hover:bg-[#F3F1EC]"
            }`}
          >
            All
          </button>
          {allGrades.map((g) => {
            const on = gradeFilter.has(g);
            return (
              <button
                key={g}
                data-testid={`wizard-grade-filter-${g}`}
                onClick={() => toggleGrade(g)}
                className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${
                  on
                    ? "bg-[#1E3F33] text-white border-[#1E3F33]"
                    : "bg-white text-[#6B7280] border-[#d4d2cc] hover:bg-[#F3F1EC]"
                }`}
              >
                Gr {g}
              </button>
            );
          })}
        </div>
      )}
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-[#4B5563]">
          {gradeFilter.size === 0 ? "Caseload" : "Filtered"} ({students.length})
        </p>
        <button
          onClick={toggleAll}
          data-testid="wizard-toggle-all"
          className="text-[11px] text-[#1E3F33] hover:underline"
        >
          {allSelected ? "Deselect all" : "Select all"}
        </button>
      </div>
      {/* FP3-069 follow-up #11 — Caseload/Wizard pre-flight banner.
          Surfaces data-hygiene gaps (missing school, missing grade,
          missing/non-canonical attendance pattern) on the SELECTED
          students BEFORE the wizard runs so operators can fix the
          student profile up front instead of debugging silently
          dropped placements after the fact. The detailed per-row
          breakdown lives in the Grade Rules Check section on the
          Constraints step. */}
      {preflightSummary && preflightSummary.warning_total > 0 && (
        <div
          data-testid="wizard-preflight-banner"
          className="rounded-md bg-[#FDF2F2] border border-[#E6C2C2] px-3 py-2 text-[11px] text-[#7A2E2E]"
        >
          <p className="font-semibold flex items-center gap-1.5">
            <Warning size={12} weight="fill" className="text-[#B85C5C]" />
            <span data-testid="wizard-preflight-total">
              {preflightSummary.warning_total} student
              {preflightSummary.warning_total === 1 ? "" : "s"} need
              {preflightSummary.warning_total === 1 ? "s" : ""} attention before scheduling
            </span>
          </p>
          <ul className="mt-1 ml-4 list-disc text-[10.5px] text-[#7A2E2E]/90 space-y-0.5">
            {preflightSummary.missing_pattern > 0 && (
              <li data-testid="wizard-preflight-pattern">
                {preflightSummary.missing_pattern} missing or non-canonical attendance pattern
              </li>
            )}
            {preflightSummary.missing_school > 0 && (
              <li data-testid="wizard-preflight-school">
                {preflightSummary.missing_school} missing / unresolved school
              </li>
            )}
            {preflightSummary.missing_grade > 0 && (
              <li data-testid="wizard-preflight-grade">
                {preflightSummary.missing_grade} missing grade
              </li>
            )}
            {preflightSummary.missing_commitment > 0 && (
              <li data-testid="wizard-preflight-commitment">
                {preflightSummary.missing_commitment} missing active service commitment
              </li>
            )}
            {preflightSummary.missing_assignment > 0 && (
              <li data-testid="wizard-preflight-assignment">
                {preflightSummary.missing_assignment} missing provider / team assignment
              </li>
            )}
          </ul>
          <p className="mt-1 text-[10px] italic text-[#7A2E2E]/80">
            See the Grade Rules Check on the Constraints step for the
            per-student breakdown. Fix the student profile, then re-open
            the wizard to clear these warnings.
          </p>
          {/* FP3-072 — Contextual help on Active Therapy + Wizard. */}
          <div className="mt-1.5">
            <HelpLink
              articleId="active-therapy"
              label="Learn about Active Therapy & the Wizard"
              testId="wizard-preflight-help"
              className="text-[10.5px] !text-[#7A2E2E] hover:!text-[#5A1F1F]"
            />
          </div>
        </div>
      )}
      {preflightLoading && !preflightSummary && (
        <div
          data-testid="wizard-preflight-loading"
          className="rounded-md bg-[#F3F1EC] border border-[#E5E3DB] px-3 py-2 text-[11px] text-[#6B7280] italic"
        >
          Checking selected students for missing school, grade, or attendance pattern…
        </div>
      )}
      {/* FP3-069 follow-up #9 — Scope summary: total caseload,
          assistant-covered excluded from default, current selected.
          Helps Pete see at a glance that the 5 Max-served students
          are still on his caseload/oversight but won't be
          duplicate-scheduled by his wizard run. */}
      {(() => {
        const assistantCoveredCount = students.filter((s) => s.assistant_covered).length;
        const selectedCount = students.filter((s) => selectedIds.has(s.id)).length;
        if (assistantCoveredCount === 0) return null;
        return (
          <div
            data-testid="wizard-scope-summary"
            className="rounded-md bg-[#F3F7F4] border border-[#D9E5DC] px-3 py-2 text-[11px] text-[#1E3F33]"
          >
            <span className="font-semibold">{students.length}</span> in caseload
            <span className="mx-1.5 text-[#8FA998]">·</span>
            <span className="font-semibold">{assistantCoveredCount}</span> assistant-covered (excluded by default)
            <span className="mx-1.5 text-[#8FA998]">·</span>
            <span className="font-semibold">{selectedCount}</span> selected for direct scheduling
          </div>
        );
      })()}
      <div className="border border-[#E5E3DB] rounded-lg bg-white divide-y divide-[#ECEAE4]">
        {students.map((s) => {
          const color = gradeColor(s.grade);
          const checked = selectedIds.has(s.id);
          const ob = s.service_obligations?.[0];
          const isAssistantCovered = s.assistant_covered === true;
          const assistantNames = s.assistant_covered_provider_names || [];
          const assistantLabel = assistantNames.length === 1
            ? assistantNames[0]
            : (assistantNames.length > 1 ? assistantNames.join(", ") : "Assistant");
          return (
            <label
              key={s.id}
              data-testid={`wizard-student-${s.id}`}
              data-assistant-covered={isAssistantCovered ? "true" : "false"}
              className={`flex items-center gap-3 px-3 py-2 cursor-pointer ${
                isAssistantCovered
                  ? "bg-[#F8F7F2] hover:bg-[#F3F1EC]"
                  : "hover:bg-[#F9F8F6]"
              }`}
            >
              <Checkbox checked={checked} onCheckedChange={() => toggleStudent(s.id)} />
              <div
                className="w-2 h-2 rounded-full shrink-0"
                style={{ backgroundColor: color.border }}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-medium truncate ${
                    isAssistantCovered ? "text-[#6B7280]" : "text-[#1F2937]"
                  }`}>
                    {s.first_name} {s.last_name}
                  </span>
                  {isAssistantCovered && (
                    <span
                      data-testid={`wizard-student-${s.id}-assistant-badge`}
                      className="inline-flex items-center gap-1 text-[9.5px] uppercase tracking-wider font-semibold text-[#1E3F33] bg-[#E8F0EB] rounded-full px-1.5 py-0.5"
                    >
                      Assigned to {assistantLabel}
                    </span>
                  )}
                </div>
                {isAssistantCovered && (
                  <p
                    data-testid={`wizard-student-${s.id}-assistant-helper`}
                    className="text-[10px] text-[#8FA998] mt-0.5"
                  >
                    Scheduled by assistant; excluded from direct schedule by default.
                  </p>
                )}
              </div>
              <span className="text-[10px] text-[#6B7280] shrink-0">Gr {s.grade}</span>
              <AttendanceBadge pattern={s.attendance_pattern} />
              <div className="w-44 text-right">
                <ObligationLabel ob={ob} />
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}

// ── Step 2: Service Needs ─────────────────────────────────────────────────────
// Iter 32 — Read-only from the student record (per user direction). The
// only editable cell is "Group?" because it's a per-run scheduling
// preference, not a student attribute. Sessions/wk, monthly minutes,
// and duration all come straight from the source service commitment;
// to change them, the user updates Service Demographics.
function NeedsStep({ students, overrides, updateOverride }) {
  return (
    <div data-testid="wizard-step-needs" className="space-y-3">
      <p className="text-xs text-[#6B7280]">
        Service commitments come from the student record (read-only here). To change minutes or duration, edit the student's Service Demographics. Group preference is per-run and editable.
      </p>
      <div className="border border-[#E5E3DB] rounded-lg bg-white">
        <div className="grid grid-cols-[1fr_120px_90px_90px_70px] gap-2 px-3 py-2 bg-[#F3F1EC] text-[10px] font-semibold uppercase tracking-wider text-[#4B5563] border-b border-[#E5E3DB]">
          <span>Student</span>
          <span>Commitment</span>
          <span>Sessions/wk</span>
          <span>Duration</span>
          <span className="text-center">Group?</span>
        </div>
        <div className="divide-y divide-[#ECEAE4]">
          {students.map((s) => {
            const ov = overrides[s.id] || {};
            const ob = s.service_obligations?.[0];
            const color = gradeColor(s.grade);
            const isMonthly = ob?.kind === "monthly_minutes" || ob?.kind === "minutes_per_month";
            // Source-of-truth display values
            const srcSessions = ob?.delivery?.sessions_per_period;
            const srcDuration = ob?.delivery?.minutes_per_session;
            const srcMonthly = ob?.total_minutes;
            return (
              <div
                key={s.id}
                data-testid={`wizard-need-${s.id}`}
                className="grid grid-cols-[1fr_120px_90px_90px_70px] gap-2 px-3 py-2 items-center"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <div
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{ backgroundColor: color.border }}
                  />
                  <span className="text-xs font-medium text-[#1F2937] truncate">
                    {s.first_name} {s.last_name}
                  </span>
                  <span className="text-[10px] text-[#6B7280] shrink-0">Gr {s.grade}</span>
                  <AttendanceBadge pattern={s.attendance_pattern} />
                </div>
                <div className="text-[10px]">
                  <ObligationLabel ob={ob} />
                </div>
                {/* Sessions or monthly minutes — read-only display */}
                <span
                  data-testid={`wizard-sessions-readonly-${s.id}`}
                  className="text-xs text-[#4B5563] tabular-nums px-1.5 py-1 rounded bg-[#FAFAF7] border border-[#ECEAE4] text-center"
                >
                  {isMonthly
                    ? srcMonthly != null
                      ? `${srcMonthly}m/mo`
                      : "—"
                    : srcSessions != null
                    ? srcSessions
                    : "—"}
                </span>
                {/* Duration — read-only from source */}
                <span
                  data-testid={`wizard-duration-readonly-${s.id}`}
                  className="text-xs text-[#4B5563] tabular-nums px-1.5 py-1 rounded bg-[#FAFAF7] border border-[#ECEAE4] text-center"
                >
                  {srcDuration != null ? `${srcDuration}m` : "—"}
                </span>
                <div className="flex justify-center">
                  <Checkbox
                    data-testid={`wizard-group-${s.id}`}
                    checked={ov.allow_grouping ?? true}
                    onCheckedChange={(v) => updateOverride(s.id, "allow_grouping", !!v)}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Step 3: Constraints ───────────────────────────────────────────────────────
function ConstraintsStep({
  blockedTimes,
  updateBlocked,
  addBlockedTime,
  removeBlocked,
  allowedDays,
  toggleDay,
  workStart,
  setWorkStart,
  workEnd,
  setWorkEnd,
  applyGradeConstraints,
  setApplyGradeConstraints,
  gradeRulesCheck,
  applyAttendance,
  setApplyAttendance,
  applyCrossProvider,
  setApplyCrossProvider,
  applyFlexDays,
  setApplyFlexDays,
  applyProviderAvailability,
  setApplyProviderAvailability,
  providerAvailability,
  coordinateWithExisting,
  setCoordinateWithExisting,
  groupSize,
  setGroupSize,
  allowGroupOf4Rescue,
  setAllowGroupOf4Rescue,
  schedulingStyle,
  setSchedulingStyle,
  existingCount,
  relevantGradeConstraints,
  disabledGradeConstraintIds,
  toggleGradeConstraint,
  crossBlockCount,
  flexDayCount,
}) {
  return (
    <div data-testid="wizard-step-constraints" className="space-y-4">
      {/* Iter 32 — Work window + Allowed days removed from this step.
          Both are already known from source truth (Provider Availability
          + School Schedules). The Wizard reads them automatically; what
          remains here are the simpler "apply or skip" toggles. */}
      <p className="text-xs text-[#6B7280]">
        Work hours and allowed days come from your Provider Rules and the school's settings — already loaded automatically. Use the toggles below to decide which rules this draft should respect.
      </p>
      {/* FP3-072 — Contextual help on the School Rules vs Provider
          Rules separation. */}
      <div className="-mt-2">
        <HelpLink
          articleId="school-vs-provider-rules"
          label="Learn the difference between School Rules and Provider Rules"
          testId="wizard-constraints-help"
        />
      </div>

      {/* Smart-constraint toggles */}
      <div className="border border-[#E5E3DB] rounded-lg bg-white p-3 space-y-2">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-[#4B5563]">
          School-aware constraints
        </p>
        <ToggleRow
          testid="wizard-apply-attendance"
          checked={applyAttendance}
          onChange={setApplyAttendance}
          icon={<Sun size={12} weight="bold" className="text-[#D9A05B]" />}
          label="Honor student attendance pattern (AM / PM / Full Day)"
        />
        <ToggleRow
          testid="wizard-apply-grade"
          checked={applyGradeConstraints}
          onChange={setApplyGradeConstraints}
          icon={<Calendar size={12} weight="bold" className="text-[#1E3F33]" />}
          label={`Apply grade-based templates (${relevantGradeConstraints.length} rules apply)`}
        />
        <ToggleRow
          testid="wizard-apply-cross-provider"
          checked={applyCrossProvider}
          onChange={setApplyCrossProvider}
          icon={<Lightning size={12} weight="bold" className="text-[#5C7088]" />}
          label={`Avoid cross-provider conflicts (${crossBlockCount} other-provider blocks at this campus)`}
        />
        <ToggleRow
          testid="wizard-apply-flex-days"
          checked={applyFlexDays}
          onChange={setApplyFlexDays}
          icon={<Sun size={12} weight="bold" className="text-[#D9A05B]" />}
          label={`Honor school flex days (${flexDayCount} configured · half-days, closures)`}
        />
        <ToggleRow
          testid="wizard-apply-provider-availability"
          checked={applyProviderAvailability}
          onChange={setApplyProviderAvailability}
          icon={<Calendar size={12} weight="bold" className="text-[#1E3F33]" />}
          label={
            providerAvailability
              ? `Honor my availability (${(providerAvailability.available_days || [0,1,2,3,4]).length} day${(providerAvailability.available_days || [0,1,2,3,4]).length !== 1 ? "s" : ""}, ${(providerAvailability.blocked_times || []).length} blocked time${(providerAvailability.blocked_times || []).length !== 1 ? "s" : ""})`
              : "Honor my availability (none configured — using full Mon–Fri)"
          }
        />
        <ToggleRow
          testid="wizard-coordinate-existing"
          checked={coordinateWithExisting}
          onChange={setCoordinateWithExisting}
          icon={<Calendar size={12} weight="bold" className="text-[#1E3F33]" />}
          label={
            existingCount > 0
              ? `Avoid existing student service blocks (${existingCount} already saved)`
              : "Avoid existing student service blocks (none yet)"
          }
        />
        {/* FP3-065 — Protected behaviour. Provider Rules (lunch,
            prep, personal blocks) are ALWAYS preserved. The wizard
            cannot overwrite or delete them, regardless of whether
            the operator picks Coordinate or Overwrite. Surfaced as
            a locked "always on" row so nobody mistakes the
            "Coordinate" toggle above for personal-block control. */}
        <div
          data-testid="wizard-provider-rules-protected"
          className="flex items-start gap-2 py-1.5 px-2 rounded-md bg-[#F3F1EC] border border-[#E5E3DB]"
        >
          <div className="mt-0.5">
            <Calendar size={12} weight="bold" className="text-[#1E3F33]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[12px] text-[#1F2937] font-medium">
              Provider Rules are always preserved
            </p>
            <p className="text-[10.5px] text-[#6B7280] leading-snug">
              Lunch, prep, meeting and other personal blocks set on
              Provider Rules are never overwritten by the wizard,
              even when you choose Overwrite below.
            </p>
          </div>
        </div>
      </div>

      {/* FP3-065 — Preferred group size. Default 3. Wizard caps
          group buckets at this number; sub-1-cap forces individual
          sessions. */}
      <div className="border border-[#E5E3DB] rounded-lg bg-white p-3 space-y-2">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-[#4B5563]">
          Preferred group size
        </p>
        <div className="flex items-center gap-2 flex-wrap" data-testid="wizard-group-size-control">
          {[1, 2, 3, 4].map((n) => {
            const active = groupSize === n;
            return (
              <button
                key={n}
                type="button"
                data-testid={`wizard-group-size-${n}`}
                data-active={active ? "true" : "false"}
                onClick={() => setGroupSize(n)}
                className={
                  "h-8 px-3 text-[12px] rounded-md border transition-colors "
                  + (active
                    ? "bg-[#1E3F33] text-white border-[#1E3F33]"
                    : "bg-white text-[#1F2937] border-[#E5E3DB] hover:border-[#1E3F33]")
                }
              >
                {n === 1 ? "1 (individual)" : n}
              </button>
            );
          })}
        </div>
        <p className="text-[10.5px] text-[#6B7280] leading-snug">
          The wizard will try to group students up to this size when
          schedules and constraints allow. Smaller groups are still
          possible when constraints require it.
        </p>
      </div>

      {/* FP3-065 follow-up #6 improvement — Group-of-4 rescue toggle.
          When ON, the wizard runs the main loop with the configured
          group-size cap (typically 3) and, as a fallback strategy
          ONLY, attaches under-scheduled students into compatible
          draft groups of 3 → 4 to complete commitments. */}
      <div className="border border-[#E5E3DB] rounded-lg bg-white p-3 space-y-2">
        <ToggleRow
          testid="wizard-allow-group-of-4-rescue"
          checked={allowGroupOf4Rescue}
          onChange={setAllowGroupOf4Rescue}
          icon={<UsersThree size={12} weight="bold" className="text-[#1E3F33]" />}
          label="Allow group of 4 when needed to complete commitments"
        />
        <p className="text-[10.5px] text-[#6B7280] leading-snug pl-6">
          Only kicks in when an under-scheduled student has no legal
          open slot. The wizard will look for a compatible group of 3
          that can legally become a group of 4. Never exceeds 4
          members. Respects all other constraints. Review step flags
          any rescues before you apply.
        </p>
      </div>

      {/* FP3-065 follow-up #7 — Scheduling style. Changes ONLY the
          ordering rules among MULTIPLE LEGAL placements; never
          affects hard constraints, commitment math, provider rules,
          or group-size cap. Default "Balanced" matches the legacy
          behaviour. */}
      <div className="border border-[#E5E3DB] rounded-lg bg-white p-3 space-y-2">
        <div className="flex items-center gap-1.5">
          <Calendar size={12} weight="bold" className="text-[#1E3F33]" />
          <p className="text-[11px] font-semibold text-[#1F2937]">
            Scheduling style
          </p>
        </div>
        <p className="text-[10.5px] text-[#6B7280] leading-snug">
          Choose how the wizard should arrange sessions when more
          than one legal schedule is possible.
        </p>
        <div
          className="grid grid-cols-3 gap-1.5"
          data-testid="wizard-scheduling-style"
          role="radiogroup"
          aria-label="Scheduling style"
        >
          {[
            { key: "compact", label: "Compact", hint: "Group sessions closer together." },
            { key: "balanced", label: "Balanced", hint: "Use a moderate schedule." },
            { key: "spread_out", label: "Spread out", hint: "Distribute sessions with more breathing room." },
          ].map((opt) => {
            const active = schedulingStyle === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => setSchedulingStyle(opt.key)}
                data-testid={`wizard-style-${opt.key}`}
                className={`text-left px-2 py-1.5 rounded-md border transition-colors ${
                  active
                    ? "border-[#1E3F33] bg-[#EEF2EC]"
                    : "border-[#E5E3DB] bg-white hover:bg-[#F7F5EE]"
                }`}
              >
                <p
                  className={`text-[11px] font-semibold ${
                    active ? "text-[#1E3F33]" : "text-[#1F2937]"
                  }`}
                >
                  {opt.label}
                </p>
                <p className="text-[10px] text-[#6B7280] leading-snug">
                  {opt.hint}
                </p>
              </button>
            );
          })}
        </div>
      </div>


      {/* FP3-066b — Grade Rules Check pre-flight indicator with full
          breakdown. One row per (school, grade) touched by the run.
          Shows BOTH `applicable_count` (what the wizard will enforce
          for the selected students' attendance patterns) AND
          `total_saved` (what's saved across AM+PM+Full Day on School
          Rules) so the operator immediately sees whether all saved
          rules will apply. `partial` and `warning` statuses surface
          configuration gaps (e.g., lunch saved only under AM but the
          selected students are Full Day) before they cause silent
          mis-scheduling. */}
      {applyGradeConstraints && (gradeRulesCheck.rows.length > 0 || gradeRulesCheck.loading) && (
        <div className="border border-[#E5E3DB] rounded-lg bg-white p-3 space-y-2" data-testid="wizard-grade-rules-check">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-[#4B5563]">
            Grade rules check
          </p>
          {gradeRulesCheck.loading ? (
            <p className="text-[11px] text-[#6B7280]">Resolving school + grade rules…</p>
          ) : (
            <div className="space-y-1.5">
              {gradeRulesCheck.rows.map((row, idx) => {
                // Status palette — warning/partial draws attention,
                // ok / no_rules stay quiet.
                const dotColor =
                  row.status === "warning" ? "#B85C5C"
                  : row.status === "partial" ? "#C18A3B"
                  : row.status === "ok" ? "#1E7A4D"
                  : "#9B9890";
                const textColor =
                  row.status === "warning" ? "#7A2E2E"
                  : row.status === "partial" ? "#7A4E1E"
                  : row.status === "ok" ? "#1F2937"
                  : "#6B7280";
                const applicable = row.applicable_count ?? row.constraint_count ?? 0;
                const totalSaved = row.total_saved ?? applicable;
                const applicablePatterns = row.applicable_patterns || [];
                const patternLabel = applicablePatterns
                  .map((p) => p === "full_day" ? "Full Day" : p.toUpperCase())
                  .join(" + ") || "selected pattern";
                const summary =
                  row.status === "warning" && row.warning_reason
                    ? row.warning_reason
                    : row.status === "no_rules"
                    ? "No grade-specific rules configured"
                    : `${applicable} applicable for ${patternLabel}` +
                      (totalSaved > applicable
                        ? ` · ${totalSaved} saved across all patterns`
                        : "");
                const partialNote =
                  row.status === "partial"
                    ? `Only ${applicable} of ${totalSaved} saved rules apply to the selected ${patternLabel} student${
                        applicablePatterns.length === 1 ? "" : "s"
                      }. The other rules are saved under ${Object.entries(
                        row.by_pattern || {}
                      )
                        .filter(
                          ([p, n]) => n > 0 && !applicablePatterns.includes(p)
                        )
                        .map(([p]) =>
                          p === "full_day" ? "Full Day" : p.toUpperCase()
                        )
                        .join(" / ")} only.`
                    : null;
                const sample = (row.sample_labels || []).join(" · ");
                // Build a tooltip-friendly title from the by-pattern
                // and by-kind breakdowns so power users can hover for
                // the full picture without us needing a popover.
                const titleLines = [];
                if (row.by_pattern) {
                  for (const [p, n] of Object.entries(row.by_pattern)) {
                    if (!n) continue;
                    const lbl = p === "full_day" ? "Full Day" : p.toUpperCase();
                    const labels = (row.labels_by_pattern?.[p] || []).join("; ");
                    titleLines.push(`${lbl} (${n}): ${labels || "—"}`);
                  }
                }
                if (row.by_kind && Object.keys(row.by_kind).length) {
                  const byKind = Object.entries(row.by_kind)
                    .map(([k, n]) => `${k} ×${n}`)
                    .join(", ");
                  titleLines.push(`By kind: ${byKind}`);
                }
                return (
                  <div
                    key={`${row.school_id || "?"}-${row.grade}-${idx}`}
                    className="flex items-start gap-2"
                    data-testid={`wizard-grade-rules-row-${idx}`}
                    title={titleLines.join("\n")}
                  >
                    <span
                      className="inline-block w-2 h-2 rounded-full mt-1 flex-shrink-0"
                      style={{ backgroundColor: dotColor }}
                    />
                    <div className="flex-1 leading-snug">
                      <p className="text-[11px]" style={{ color: textColor }}>
                        <span className="font-semibold">
                          Grade {row.grade || "?"} @ {row.school_name || "unknown"}
                        </span>
                        <span className="text-[#6B7280]"> · {summary}</span>
                      </p>
                      {partialNote && (
                        <p
                          className="text-[10px] italic mt-0.5"
                          style={{ color: "#7A4E1E" }}
                          data-testid={`wizard-grade-rules-row-${idx}-partial-note`}
                        >
                          {partialNote}
                        </p>
                      )}
                      {sample && (
                        <p className="text-[10px] text-[#6B7280] italic mt-0.5">{sample}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}


      {applyGradeConstraints && relevantGradeConstraints.length > 0 && (
        <div data-testid="wizard-grade-templates">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-[#4B5563] mb-2">
            Grade templates · click to disable for this run
          </p>
          <div className="flex flex-wrap gap-1.5">
            {relevantGradeConstraints.map((c) => {
              const disabled = disabledGradeConstraintIds.has(c.id);
              const dayLabel = c.day_of_week === -1 ? "every day" : DAY_LABELS[c.day_of_week];
              return (
                <button
                  key={c.id}
                  data-testid={`wizard-gc-${c.id}`}
                  onClick={() => toggleGradeConstraint(c.id)}
                  className={`text-[10px] px-2 py-1 rounded-md border transition-colors ${
                    disabled
                      ? "bg-white text-[#9B9890] border-[#E5E3DB] line-through"
                      : "bg-[#F2F8F4] text-[#1E3F33] border-[#D9E5DC]"
                  }`}
                  title={disabled ? "Click to re-enable" : "Click to disable for this run"}
                >
                  {c.label} · {c.start_time}–{c.end_time} · {dayLabel}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-[#4B5563]">
            Manual blocked times
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={addBlockedTime}
            data-testid="wizard-add-blocked"
            className="h-7 text-[11px] rounded-md border-[#d4d2cc]"
          >
            <Plus size={11} className="mr-1" />
            Add
          </Button>
        </div>
        <div className="space-y-1.5">
          {blockedTimes.length === 0 && (
            <p className="text-[11px] text-[#6B7280] italic">
              No additional blocks. Add lunch, specials, etc. that aren't already covered by grade templates.
            </p>
          )}
          {blockedTimes.map((b, idx) => (
            <div
              key={idx}
              data-testid={`wizard-blocked-${idx}`}
              className="flex items-center gap-2 border border-[#E5E3DB] rounded-md px-2 py-1.5 bg-white"
            >
              <select
                value={b.day_of_week}
                onChange={(e) => updateBlocked(idx, "day_of_week", parseInt(e.target.value, 10))}
                className="h-7 text-xs border border-[#d4d2cc] rounded-md px-1.5 bg-white"
              >
                <option value={-1}>Every day</option>
                {nativeOptions(DAY_LABELS, (d, i) => ({ value: i, key: d, label: d }))}
              </select>
              <Input
                type="time"
                value={b.start_time}
                onChange={(e) => updateBlocked(idx, "start_time", e.target.value)}
                className="h-7 text-xs w-24"
              />
              <span className="text-xs text-[#6B7280]">to</span>
              <Input
                type="time"
                value={b.end_time}
                onChange={(e) => updateBlocked(idx, "end_time", e.target.value)}
                className="h-7 text-xs w-24"
              />
              <Input
                value={b.label}
                onChange={(e) => updateBlocked(idx, "label", e.target.value)}
                placeholder="Label"
                className="h-7 text-xs flex-1"
              />
              <button
                onClick={() => removeBlocked(idx)}
                className="text-[#6B7280] hover:text-[#B85C5C] p-1"
                data-testid={`wizard-remove-blocked-${idx}`}
              >
                <Trash size={12} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ToggleRow({ testid, checked, onChange, icon, label }) {
  return (
    <label
      data-testid={testid}
      className="flex items-center gap-2 cursor-pointer text-[11px] text-[#1F2937]"
    >
      <Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} />
      {icon}
      <span>{label}</span>
    </label>
  );
}

// ── Step 4: Review ────────────────────────────────────────────────────────────
function ReviewStep({ result, onApplyRescueSuggestion, onSwitchStyle }) {
  const {
    proposed_blocks: blocks = [],
    unscheduled = [],
    summary = {},
    expected_vs_placed: cov = {},
    warnings = [],
    group_of_4_rescues: rescues = [],
  } = result || {};

  const blocksByDay = useMemo(() => {
    const m = { 0: [], 1: [], 2: [], 3: [], 4: [] };
    blocks.forEach((b) => m[b.day_of_week]?.push(b));
    Object.keys(m).forEach((d) => m[d].sort((a, b) => a.start_time.localeCompare(b.start_time)));
    return m;
  }, [blocks]);

  const groupKeys = new Set();
  blocks.forEach((b) => {
    const k = `${b.day_of_week}|${b.start_time}|${b.end_time}|${b.service_type}`;
    if (b.group_size > 1) groupKeys.add(k);
  });

  const placedRatio = summary.total_required_blocks
    ? Math.round((summary.total_placed_blocks / summary.total_required_blocks) * 100)
    : 0;

  // Sort coverage: anything < 100% bubbles up first
  const coverageRows = useMemo(
    () =>
      Object.entries(cov)
        .map(([sid, ev]) => ({ sid, ...ev }))
        .sort((a, b) => a.coverage_pct - b.coverage_pct),
    [cov]
  );

  // FP3-065 follow-up #6 (improvement) — Per-student "actual placed
  // pattern" chip derived from the wizard's proposed_blocks instead
  // of the obligation-side `recommended_pattern`. Groups same-shape
  // blocks (same duration AND same weeks_of_month) into a single
  // phrase like `2 × 30m on weeks 1,2,3`. Falls back gracefully:
  // - single same-shape block → `30m on weeks 1,2,3`
  // - weekly-cadence (no weeks_of_month) → `2 × 30m/week`
  // - mixed shapes (rare) → joined with `+`, e.g.
  //     `30m on weeks 1,2,3 + 30m on weeks 1,2`
  // - student has zero blocks (unplaced) → falls back to the
  //   obligation-side `recommended_pattern` in the chip render.
  const placedPatternByStudent = useMemo(() => {
    const byStudent = new Map();
    (blocks || []).forEach((b) => {
      const sid = Array.isArray(b.student_ids) ? b.student_ids[0] : null;
      if (!sid) return;
      const wom = Array.isArray(b.weeks_of_month) && b.weeks_of_month.length
        ? [...b.weeks_of_month].sort((x, y) => x - y)
        : null;
      if (!byStudent.has(sid)) byStudent.set(sid, new Map());
      const groups = byStudent.get(sid);
      const womKey = wom ? wom.join(",") : "weekly";
      const key = `${b.duration_min}|${womKey}`;
      if (!groups.has(key)) {
        groups.set(key, { count: 0, dur: b.duration_min, wom });
      }
      groups.get(key).count += 1;
    });
    const out = {};
    byStudent.forEach((groups, sid) => {
      const phrases = Array.from(groups.values()).map(({ count, dur, wom }) => {
        const prefix = count > 1 ? `${count} × ${dur}m` : `${dur}m`;
        return wom ? `${prefix} on weeks ${wom.join(",")}` : `${prefix}/week`;
      });
      out[sid] = phrases.join(" + ");
    });
    return out;
  }, [blocks]);

  // Unique cross-provider conflicts mentioned in unscheduled reasons
  const crossProviderConflicts = unscheduled.filter((u) =>
    typeof u.reason === "string" && u.reason.includes("would conflict")
  );

  // FP3-065 follow-up #5 — Same-day fallback warnings. Group by
  // student so the panel reads cleanly when a student has 2+
  // sessions placed on the same weekday.
  // FP3-065 follow-up #6 improvement — Include the actual placed
  // shape on the fallback day(s) so the card reads
  //   "Olivia Pisaturo · 2 × 30m on Monday · attempted Tue–Fri but
  //    no legal slot · same-day fallback."
  // The shape is derived from `proposed_blocks` (the same source the
  // per-student cadence chip uses) and the attempted-days line is
  // derived from the backend's per-fresh-day rejection reasons —
  // pure UI surfacing, zero scheduling behavior change.
  const DAY_FULL_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

  // FP3-065 follow-up #6 improvement — Convert a list of day_of_week
  // ints into a human range string. Contiguous days collapse into
  // "Tue–Fri"; disjoint days join with ", " (e.g. "Tue, Thu").
  const formatAttemptedDayRange = (dayInts) => {
    if (!dayInts || !dayInts.length) return "";
    const sorted = [...new Set(dayInts)].sort((a, b) => a - b);
    const ranges = [];
    let runStart = sorted[0];
    let runEnd = sorted[0];
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i] === runEnd + 1) {
        runEnd = sorted[i];
      } else {
        ranges.push([runStart, runEnd]);
        runStart = sorted[i];
        runEnd = sorted[i];
      }
    }
    ranges.push([runStart, runEnd]);
    return ranges
      .map(([s, e]) =>
        s === e
          ? DAY_FULL_NAMES[s] || `Day ${s}`
          : `${DAY_FULL_NAMES[s] || `Day ${s}`}–${DAY_FULL_NAMES[e] || `Day ${e}`}`
      )
      .join(", ");
  };

  // Pick the most useful single reason string from the per-fresh-day
  // rejection list. Prioritises domain-specific causes ("provider
  // rule" / "lunch" / "attendance window") over generic ones so the
  // operator sees the actionable cause when it's available.
  const pickAttemptedReason = (attemptedDays) => {
    if (!attemptedDays || !attemptedDays.length) return "";
    const priority = (r) => {
      const s = (r || "").toLowerCase();
      if (s.includes("session that day")) return 7.5;
      if (s.includes("provider rule") || s.includes("week mask")) return 7;
      // FP3-065 follow-up #7 audit — student constraints surface
      // with labels like "<operator label> (other)" or "student rule
      // (other)" — match both shapes so the compromise / unscheduled
      // / amber cards cite the actual cause.
      if (s.includes("student rule") || s.includes("student constraint")) return 6.5;
      if (s.includes("group") || s.includes("compatible")) return 5;
      if (s.includes("lunch") || s.includes("recess") || s.includes("specials")) return 4;
      if (s.includes("attendance") || s.includes("grade")) return 3;
      if (s.includes("teacher") || s.includes("flex")) return 2;
      if (s.includes("conflict") || s.includes("double-book")) return 1;
      return 0;
    };
    const sorted = [...attemptedDays].sort(
      (a, b) => priority(b.reason) - priority(a.reason)
    );
    return sorted[0].reason || "no legal open slot";
  };

  // FP3-106 — Same-day fallback removed: the one-session-per-day rule
  // is HARD platform-wide. The wizard never emits same_day_fallback
  // warnings anymore; unmet remainders surface in the unscheduled
  // panel with reason_code `insufficient_distinct_legal_days`.

  return (
    <div data-testid="wizard-step-review" className="space-y-4">
      {/* FP3-065 — Surface the group size cap used by this run so
          operators see what the wizard honored. Defaults to 3 from
          the constraints step. */}
      <p
        data-testid="wizard-review-group-size"
        className="text-[11px] text-[#4B5563]"
      >
        Group size cap: <strong>{summary.group_size_cap || 3}</strong>
        {summary.group_size_cap === 1 ? " (individual sessions only)" : ""}
      </p>
      {/* FP3-065 follow-up #7 — Selected scheduling style chip. */}
      {summary.scheduling_style && (
        <p
          data-testid="wizard-review-scheduling-style"
          className="text-[11px] text-[#4B5563]"
        >
          Scheduling style:{" "}
          <strong>
            {summary.scheduling_style === "compact"
              ? "Compact"
              : summary.scheduling_style === "spread_out"
              ? "Spread out"
              : "Balanced"}
          </strong>
        </p>
      )}

      {/* FP3-065 follow-up #7 — Compromise summary. Shown when the
          selected style couldn't be fully honored because of hard
          constraints. Derived from existing backend signals (style
          echo + same-day warnings + days used + unscheduled
          reasons) so no extra wire format is needed. Renders ONLY
          when a real compromise is detected — no false positives. */}
      {(() => {
        const style = summary.scheduling_style;
        if (!style) return null;
        const daysUsed = new Set(
          (blocks || []).map((b) => b.day_of_week)
        ).size;
        // Best available reason from unscheduled attempted-days
        // reasons. Mirrors the priority sort the amber card uses,
        // falling back to a generic phrase.
        const unschedReasons = (unscheduled || [])
          .flatMap((u) => u?.attempted_days || [])
          .map((a) => (a.reason || "").toLowerCase())
          .concat((unscheduled || []).map((u) => (u?.reason || "").toLowerCase()));
        const allReasons = unschedReasons;
        const pickReason = () => {
          const has = (kw) => allReasons.some((r) => r.includes(kw));
          if (has("session that day")) return "the one-session-per-day rule";
          if (has("provider rule") || has("week mask")) return "provider rules";
          // FP3-065 follow-up #7 audit — student constraints first
          // class. Match both the operator-set label-only path AND
          // the wizard's "<label> (kind)" composed reason.
          if (has("student rule") || has("student constraint")) return "student-specific constraints";
          if (has("attendance") || has("grade")) return "school or grade window";
          if (has("lunch") || has("recess") || has("specials")) return "lunch / specials coverage";
          if (has("teacher") || has("flex")) return "teacher or flex-day constraints";
          if (has("group") || has("compatible")) return "group-size limit";
          if (has("conflict") || has("double-book")) return "calendar conflicts";
          if (allReasons.length > 0) return "hard constraints";
          return null;
        };
        const reason = pickReason();
        let message = null;
        if (style === "compact" && daysUsed > 1 && reason) {
          // Only flag compact compromises when there's evidence
          // (a reason from attempted_days) that hard constraints
          // forced the spread. A wide-window run that naturally
          // packs tight should not surface anything.
          message = (
            <>
              <span className="font-semibold">Compact limited:</span>{" "}
              sessions were spread across {daysUsed} days because of {reason}.
            </>
          );
        }
        if (!message) return null;
        // FP3-065 follow-up #7 — One-click style swaps. Show the
        // two OTHER styles as small "Try X instead" buttons inline
        // in the compromise card.
        const STYLE_LABELS = {
          compact: "Compact",
          balanced: "Balanced",
          spread_out: "Spread out",
        };
        const otherStyles = ["compact", "balanced", "spread_out"].filter(
          (s) => s !== style
        );
        return (
          <div
            className="border border-[#E8DBB0] rounded-lg bg-[#FBF6E6] p-3 flex items-start gap-2"
            data-testid="wizard-style-compromise"
          >
            <Warning size={14} weight="fill" className="text-[#9A7B17] mt-0.5" />
            <div className="flex-1 space-y-2">
              <p className="text-[11px] text-[#1F2937] leading-snug">{message}</p>
              {onSwitchStyle && (
                <div className="flex flex-wrap items-center gap-1.5">
                  {otherStyles.map((s) => (
                    <Button
                      key={s}
                      size="sm"
                      onClick={() => onSwitchStyle(s)}
                      data-testid={`wizard-try-style-${s}`}
                      className="bg-white hover:bg-[#FBF6E6] text-[#9A7B17] border border-[#E8DBB0] rounded-md h-6 text-[10.5px] px-2"
                    >
                      Try {STYLE_LABELS[s]} instead
                    </Button>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })()}
      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-2">
        <SummaryCard
          label="Placed"
          value={`${summary.total_placed_blocks}/${summary.total_required_blocks}`}
          subtitle={`${placedRatio}% of required`}
        />
        <SummaryCard
          label="Groups"
          value={groupKeys.size}
          subtitle="distinct group meetings"
        />
        <SummaryCard
          label="Students"
          value={summary.students_in_input}
          subtitle="in this draft"
        />
        <SummaryCard
          label="Unscheduled"
          value={summary.unscheduled_block_count}
          subtitle="unmet blocks"
          warn={summary.unscheduled_block_count > 0}
        />
      </div>

      {/* Constraint badges applied */}
      <div className="flex flex-wrap gap-1.5 text-[10px]">
        {summary.applied_attendance_window && (
          <span className="px-2 py-0.5 rounded bg-[#FDF8F3] text-[#D9A05B] border border-[#F2DBC0]">
            ✓ Attendance windows
          </span>
        )}
        {summary.applied_grade_constraints && (
          <span className="px-2 py-0.5 rounded bg-[#F2F8F4] text-[#1E3F33] border border-[#D9E5DC]">
            ✓ Grade templates
          </span>
        )}
        {summary.applied_cross_provider_conflicts && (
          <span className="px-2 py-0.5 rounded bg-[#EEF2F7] text-[#5C7088] border border-[#D6DEE8]">
            ✓ Cross-provider awareness
          </span>
        )}
      </div>

      {/* FP3-106 — Grouping-quality summary: group-size distribution,
          compaction actions taken, and a machine-readable reason for
          every remaining individual session. */}
      {summary.grouping_quality && (() => {
        const gq = summary.grouping_quality;
        const dist = gq.session_group_sizes || {};
        const distPhrase = Object.entries(dist)
          .sort((a, b) => Number(b[0]) - Number(a[0]))
          .map(([size, count]) => {
            const n = Number(size);
            if (n === 1) return `${count} individual session${count !== 1 ? "s" : ""}`;
            if (n === 2) return `${count} pair${count !== 1 ? "s" : ""}`;
            return `${count} group${count !== 1 ? "s" : ""} of ${n}`;
          })
          .join(" · ");
        const merges = Array.isArray(result?.compaction_merges)
          ? result.compaction_merges
          : [];
        const reasons = Array.isArray(gq.singleton_reasons)
          ? gq.singleton_reasons
          : [];
        const existingSingles = Array.isArray(gq.existing_individual_sessions)
          ? gq.existing_individual_sessions
          : [];
        const mergeKindLabel = (m) => {
          if (m.kind === "joined_existing") return " · joined existing session";
          if (m.kind === "relocated") return " · moved to a new shared time";
          if (m.kind === "repacked_day") return " · repacked within day";
          return "";
        };
        const downloadDiagnostics = async () => {
          try {
            const { data } = await api.getWizardDiagnosticsLatest();
            const blob = new Blob([JSON.stringify(data, null, 2)], {
              type: "application/json",
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `wizard-diagnostics-${data.wizard_run_id || "latest"}.json`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
          } catch (e) {
            toast.error(
              e?.response?.data?.detail || "Could not download diagnostics."
            );
          }
        };
        return (
          <div
            className="border border-[#E5E3DB] rounded-lg bg-white overflow-hidden"
            data-testid="wizard-grouping-quality"
          >
            <div className="px-3 py-2 bg-[#F3F1EC] border-b border-[#E5E3DB] flex items-center gap-1.5">
              <UsersThree size={13} className="text-[#1E3F33]" />
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[#4B5563]">
                Grouping quality
              </p>
              <button
                type="button"
                onClick={downloadDiagnostics}
                data-testid="wizard-download-diagnostics"
                className="ml-auto text-[10px] font-semibold text-[#1E3F33] underline decoration-dotted hover:opacity-70"
              >
                Download diagnostics
              </button>
            </div>
            <div className="px-3 py-2 space-y-2">
              <p
                className="text-[11px] text-[#1F2937]"
                data-testid="wizard-grouping-distribution"
              >
                {distPhrase || "No sessions placed."}
              </p>
              {merges.length > 0 && (
                <div className="space-y-0.5" data-testid="wizard-compaction-summary">
                  <p className="text-[10.5px] text-[#1E3F33] font-semibold">
                    Compaction merged {merges.length} session{merges.length !== 1 ? "s" : ""} into groups:
                  </p>
                  {merges.slice(0, 8).map((m, i) => (
                    <p key={i} className="text-[10.5px] text-[#4B5563]">
                      {(m.moved_student_names || []).join(", ")}
                      {" → "}
                      {DAY_LABELS[m.to?.day_of_week] || `Day ${m.to?.day_of_week}`}{" "}
                      {fmtTime(m.to?.start_time)} (group of {m.new_group_size})
                      {m.resulted_in_group_of_4 ? " · group-of-4 rescue" : ""}
                      {mergeKindLabel(m)}
                    </p>
                  ))}
                  {merges.length > 8 && (
                    <p className="text-[10px] text-[#6B7280] italic">
                      + {merges.length - 8} more
                    </p>
                  )}
                </div>
              )}
              {reasons.length > 0 && (
                <div className="space-y-0.5">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-[#6B7280]">
                    Why individual?
                  </p>
                  {reasons.slice(0, 10).map((r) => (
                    <p
                      key={r.block_id}
                      className="text-[10.5px] text-[#4B5563]"
                      data-testid={`wizard-singleton-reason-${r.student_id}`}
                    >
                      <span className="font-semibold text-[#1F2937]">{r.student_name}</span>
                      {" · "}
                      {DAY_LABELS[r.day_of_week] || `Day ${r.day_of_week}`}{" "}
                      {fmtTime(r.start_time)}
                      {" · "}
                      <span className="italic">{r.message}</span>{" "}
                      <code className="text-[9px] bg-[#F3F1EC] rounded px-1 py-0.5">
                        {r.code}
                      </code>
                      {r.detail ? (
                        <span
                          className="block text-[10px] text-[#6B7280] italic pl-2"
                          data-testid={`wizard-singleton-detail-${r.student_id}`}
                        >
                          {r.detail}
                        </span>
                      ) : null}
                    </p>
                  ))}
                  {reasons.length > 10 && (
                    <p className="text-[10px] text-[#6B7280] italic">
                      + {reasons.length - 10} more
                    </p>
                  )}
                </div>
              )}
              {/* FP3-106A item 6 — preserved existing saved singleton
                  sessions: never attributed to the grouping optimizer. */}
              {existingSingles.length > 0 && (
                <div className="space-y-0.5" data-testid="wizard-existing-singletons">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-[#6B7280]">
                    Existing individual sessions (preserved, not created by this run)
                  </p>
                  {existingSingles.slice(0, 8).map((e) => (
                    <p
                      key={e.block_id}
                      className="text-[10.5px] text-[#4B5563]"
                      data-testid={`wizard-existing-singleton-${e.block_id}`}
                    >
                      <span className="font-semibold text-[#1F2937]">{e.student_name}</span>
                      {" · "}
                      {DAY_LABELS[e.day_of_week] || `Day ${e.day_of_week}`}{" "}
                      {fmtTime(e.start_time)}
                      {" "}
                      <code className="text-[9px] bg-[#F3F1EC] rounded px-1 py-0.5">
                        {e.code}
                      </code>
                    </p>
                  ))}
                  {existingSingles.length > 8 && (
                    <p className="text-[10px] text-[#6B7280] italic">
                      + {existingSingles.length - 8} more
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Mini week preview */}
      <div className="border border-[#E5E3DB] rounded-lg bg-[#F9F8F5] overflow-hidden">
        <div className="px-3 py-2 bg-[#ECEAE4] border-b border-[#d4d2cc]">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#4B5563]">
            Proposed weekly draft
          </p>
        </div>
        <div className="p-2 space-y-1">
          {DAY_LABELS.map((d, idx) => (
            <div key={d} className="flex items-start gap-2" data-testid={`wizard-preview-day-${idx}`}>
              <span className="text-[10px] font-bold text-[#4B5563] w-8 shrink-0 mt-1">
                {d}
              </span>
              <div className="flex-1 relative h-7 bg-white border border-[#E5E3DB] rounded">
                {blocksByDay[idx].map((b) => {
                  const sm =
                    parseInt(b.start_time.split(":")[0], 10) * 60 +
                    parseInt(b.start_time.split(":")[1], 10);
                  const em =
                    parseInt(b.end_time.split(":")[0], 10) * 60 +
                    parseInt(b.end_time.split(":")[1], 10);
                  const left = pct(sm);
                  const width = ((em - sm) / TOTAL_MIN) * 100;
                  if (left < 0 || left > 100) return null;
                  const color = GRADE_COLORS[b.students?.[0]?.grade] || GRADE_COLORS["1"];
                  return (
                    <div
                      key={b.id}
                      title={`${b.students?.[0]?.first_name} ${b.students?.[0]?.last_name} · ${fmtTime(b.start_time)}–${fmtTime(b.end_time)}${b.group_size > 1 ? ` · group ${b.group_size}` : ""}`}
                      style={{
                        left: `${left}%`,
                        width: `${width}%`,
                        backgroundColor: color.bg,
                        borderLeft: `2px solid ${color.border}`,
                      }}
                      className="absolute top-0.5 bottom-0.5 rounded text-[9px] truncate px-1 leading-none flex items-center"
                    >
                      <span style={{ color: color.text }} className="font-semibold truncate">
                        {b.students?.[0]?.first_name?.[0]}
                        {b.group_size > 1 ? `·${b.group_size}` : ""}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Per-student coverage */}
      {coverageRows.length > 0 && (
        <div className="border border-[#E5E3DB] rounded-lg bg-white overflow-hidden" data-testid="wizard-coverage">
          <div className="px-3 py-2 bg-[#F3F1EC] border-b border-[#E5E3DB]">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#4B5563]">
              Per-student coverage
            </p>
          </div>
          <div className="divide-y divide-[#ECEAE4]">
            {coverageRows.map((r) => {
              const placedPattern = placedPatternByStudent[r.sid] || "";
              const chipText = placedPattern
                || (r.obligation_kind === "monthly_minutes" ? r.recommended_pattern : "");
              return (
                <div
                  key={r.sid}
                  data-testid={`wizard-coverage-${r.sid}`}
                  className="px-3 py-2 flex items-center gap-3 text-[11px]"
                >
                  <span className="font-medium text-[#1F2937] flex-1 truncate">{r.student_name}</span>
                  {chipText && (
                    <span
                      data-testid={`wizard-coverage-pattern-${r.sid}`}
                      className="text-[10px] text-[#5C7088] bg-[#EEF2F7] px-1.5 py-0.5 rounded"
                    >
                      {chipText}
                    </span>
                  )}
                  <span className="text-[10px] text-[#6B7280] tabular-nums">
                    {r.placed_minutes_per_month}/{r.expected_minutes_per_month} min/mo
                  </span>
                  <span
                    className={`text-[11px] font-semibold tabular-nums ${
                      r.coverage_pct >= 100
                        ? "text-[#1E3F33]"
                        : r.coverage_pct > 0
                        ? "text-[#D9A05B]"
                        : "text-[#B85C5C]"
                    }`}
                  >
                    {r.coverage_pct}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* FP3-065 follow-up #6 improvement — Group-of-4 rescue
          suggestion. Shown ONLY when the toggle is currently OFF,
          the run produced unscheduled students, AND the backend's
          read-only analysis confirmed at least one of them would
          legally fit a draft group of 3 → 4. Zero false positives.
          Clicking the action button flips the toggle and re-runs
          the wizard with all other settings unchanged. */}
      {summary.group_of_4_rescue_suggested
        && summary.group_of_4_rescue_could_help_count > 0
        && (
        <div
          className="border border-[#CDDFEA] rounded-lg bg-[#EEF5FB] p-3 flex items-start gap-2"
          data-testid="wizard-rescue-suggestion"
        >
          <UsersThree size={14} weight="fill" className="text-[#1E3F66] mt-0.5" />
          <div className="flex-1 space-y-1">
            <p className="text-[11px] text-[#1F2937]">
              <span className="font-semibold">Suggested:</span>
              {" "}
              {(() => {
                // FP3-065 follow-up #7 — Show student names when
                // the backend's read-only analysis identified the
                // affected students; fall back to a count phrase
                // when names are missing.
                const wouldHelp = Array.isArray(summary.group_of_4_rescue_would_help)
                  ? summary.group_of_4_rescue_would_help
                  : [];
                if (wouldHelp.length === 0) {
                  return summary.group_of_4_rescue_could_help_count === 1
                    ? "1 unscheduled student could be placed by allowing one group of 4."
                    : `${summary.group_of_4_rescue_could_help_count} unscheduled students could be placed by allowing one group of 4.`;
                }
                if (wouldHelp.length === 1) {
                  return `${wouldHelp[0].student_name} could be placed by allowing one group of 4.`;
                }
                if (wouldHelp.length === 2) {
                  return `${wouldHelp[0].student_name} and ${wouldHelp[1].student_name} could be placed by allowing one group of 4.`;
                }
                const head = wouldHelp.slice(0, 2).map((w) => w.student_name).join(", ");
                return `${head}, and ${wouldHelp.length - 2} more could be placed by allowing one group of 4.`;
              })()}
            </p>
            <Button
              size="sm"
              onClick={onApplyRescueSuggestion}
              data-testid="wizard-rescue-suggestion-apply"
              className="bg-[#1E3F66] hover:bg-[#173052] text-white rounded-md h-7 text-[11px] px-2"
            >
              Turn on group-of-4 rescue and re-run
            </Button>
          </div>
        </div>
      )}

      {/* Unscheduled list with reasons */}
      {unscheduled.length > 0 && (
        <div
          className="border border-[#F1D6D6] rounded-lg bg-[#FDF2F2] p-3"
          data-testid="wizard-unscheduled"
        >
          <div className="flex items-center gap-1.5 mb-2">
            <Warning size={14} weight="fill" className="text-[#B85C5C]" />
            <p className="text-[11px] font-semibold uppercase tracking-wider text-[#B85C5C]">
              {unscheduled.length} unmet
            </p>
            {crossProviderConflicts.length > 0 && (
              <span className="ml-2 text-[10px] text-[#6B7280]">
                ({crossProviderConflicts.length} cross-provider conflict{crossProviderConflicts.length !== 1 ? "s" : ""})
              </span>
            )}
          </div>
          <div className="space-y-1">
            {unscheduled.slice(0, 10).map((u, i) => {
              // FP3-065 follow-up #6 improvement — Surface the same
              // attempted-days breakdown the same-day fallback card
              // uses. When `attempted_days` is populated, render
              // "attempted Mon–Fri — IEP meeting" so operators see
              // which constraint blocked the placement. Falls back
              // to the legacy single-line `u.reason` when the
              // backend didn't attach attempted_days (older runs).
              const attempted = Array.isArray(u.attempted_days) ? u.attempted_days : [];
              const attemptedRange = formatAttemptedDayRange(
                attempted.map((a) => a.day_of_week)
              );
              const attemptedReason = pickAttemptedReason(attempted);
              // FP3-106 — distinct-day exhaustion carries the exact
              // explanation ("no additional legal day was available")
              // straight from the backend reason field.
              const isDayExhaustion =
                u.reason_code === "insufficient_distinct_legal_days";
              return (
                <div
                  key={i}
                  className="text-[11px] text-[#1F2937]"
                  data-testid={`wizard-unmet-${i}`}
                >
                  <p>
                    <span className="font-semibold">{u.student_name}</span>
                    <span className="text-[#6B7280]">
                      {" "}
                      · missing {u.missing_count} × {u.duration_min}m
                      {u.recommended_pattern ? ` · ${u.recommended_pattern}` : ""}
                      {isDayExhaustion ? (
                        <>
                          {" · "}
                          <span
                            className="italic"
                            data-testid={`wizard-unmet-${i}-day-exhaustion`}
                          >
                            {u.reason}
                          </span>
                        </>
                      ) : attemptedRange ? (
                        <>
                          {" · attempted "}
                          <span className="italic">{attemptedRange}</span>
                          {attemptedReason ? (
                            <>
                              {" — "}
                              <span className="italic">{attemptedReason}</span>
                            </>
                          ) : null}
                        </>
                      ) : (
                        <>
                          {" · "}
                          <span className="italic">{u.reason}</span>
                        </>
                      )}
                    </span>
                  </p>
                  {/* FP3-069 follow-up #3 — Rule traceback. Surfaces
                      the exact school / grade / pattern / rule source
                      the wizard resolved for this student so the
                      operator can spot data bugs (wrong pattern, stale
                      legacy row, school mismatch) without paging an
                      engineer. */}
                  {u.resolution && (
                    <details
                      className="mt-1 ml-2 text-[10.5px] text-[#4B5563]"
                      data-testid={`wizard-unmet-${i}-resolution`}
                    >
                      <summary className="cursor-pointer hover:text-[#1E3F33] select-none">
                        Why? Show resolved rules
                      </summary>
                      <div className="mt-1.5 pl-2 border-l border-[#E5E3DB] space-y-1">
                        <div>
                          <span className="text-[#6B7280]">School:</span>{" "}
                          <span data-testid={`wizard-unmet-${i}-school`}>
                            {u.resolution.school_name || "—"}
                          </span>
                          {" · "}
                          <span className="text-[#6B7280]">Grade:</span>{" "}
                          <span>{u.resolution.grade || "—"}</span>
                          {" · "}
                          <span className="text-[#6B7280]">Pattern:</span>{" "}
                          <span
                            data-testid={`wizard-unmet-${i}-pattern`}
                            className="font-semibold uppercase tracking-wider"
                          >
                            {u.resolution.attendance_pattern || "—"}
                          </span>
                        </div>
                        {u.attendance_window && (
                          <div>
                            <span className="text-[#6B7280]">
                              Attendance window:
                            </span>{" "}
                            <span
                              data-testid={`wizard-unmet-${i}-attendance-window`}
                            >
                              {u.attendance_window.start_time}–{u.attendance_window.end_time}
                            </span>
                          </div>
                        )}
                        <div>
                          <span className="text-[#6B7280]">
                            Rule source:
                          </span>{" "}
                          <code
                            data-testid={`wizard-unmet-${i}-source`}
                            className="text-[10px] bg-[#F3F1EC] rounded px-1 py-0.5"
                          >
                            {u.resolution.source}
                          </code>
                        </div>
                        {Array.isArray(u.resolution.rules_applied) &&
                          u.resolution.rules_applied.length > 0 && (
                            <div>
                              <span className="text-[#6B7280]">
                                Applied rules ({u.resolution.rules_applied.length}):
                              </span>
                              <ul
                                data-testid={`wizard-unmet-${i}-rules`}
                                className="mt-0.5 ml-3 list-disc text-[10.5px] space-y-0.5"
                              >
                                {u.resolution.rules_applied.map((r, j) => {
                                  const dayLabel =
                                    r.day_of_week === -1
                                      ? "Mon–Fri"
                                      : DAY_LABELS[r.day_of_week] || `Day ${r.day_of_week}`;
                                  const labelText =
                                    (r.label && r.label.trim()) || `(${r.kind})`;
                                  return (
                                    <li key={j}>
                                      {labelText} · {dayLabel} ·{" "}
                                      <span className="font-mono">
                                        {r.start_time}–{r.end_time}
                                      </span>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          )}
                        {u.resolution.source === "no_school_match" && (
                          <p className="text-[#A8453A] mt-1">
                            ⚠ Student&rsquo;s campus/school did not match any
                            school on this workspace. Wizard ran with no
                            grade rules and no attendance window. Check the
                            student&rsquo;s campus / school_id.
                          </p>
                        )}
                        {u.resolution.source === "legacy_grade_constraints" && (
                          <p className="text-[#9A7B17] mt-1">
                            ⚠ Falling back to the legacy
                            <code className="font-mono">grade_constraints</code>
                            {" "}collection. The current{" "}
                            <code className="font-mono">grade_schedules</code>
                            {" "}row for this (school, grade) pair is missing.
                            Open Schedule → School Rules → Grade {u.resolution.grade}
                            {" "}and re-save to migrate.
                          </p>
                        )}
                      </div>
                    </details>
                  )}
                </div>
              );
            })}
            {unscheduled.length > 10 && (
              <p className="text-[10px] text-[#6B7280] italic">
                + {unscheduled.length - 10} more
              </p>
            )}
          </div>
        </div>
      )}

      {/* FP3-106 — the same-day fallback card is gone: the
          one-session-per-day rule is HARD, so the wizard never
          places two sessions for a student on one calendar day. */}

      {/* FP3-065 follow-up #6 improvement — Group-of-4 rescue ledger.
          Soft-blue info card surfacing students who joined an
          existing group of 3 to become a group of 4 because no
          legal solo slot was available. Non-blocking — just
          transparency before Apply. */}
      {rescues.length > 0 && (
        <div
          className="border border-[#CDDFEA] rounded-lg bg-[#EEF5FB] p-3"
          data-testid="wizard-group-of-4-rescues"
        >
          <div className="flex items-center gap-1.5 mb-2">
            <UsersThree size={14} weight="fill" className="text-[#1E3F66]" />
            <p className="text-[11px] font-semibold uppercase tracking-wider text-[#1E3F66]">
              {rescues.length} group of 4 used to complete commitment{rescues.length !== 1 ? "s" : ""}
            </p>
          </div>
          <p className="text-[11px] text-[#1F2937] mb-2">
            Student{rescues.length !== 1 ? "s" : ""} added to an existing group of 3 to meet required minutes when no legal solo slot was available.
          </p>
          <div className="space-y-1">
            {rescues.slice(0, 10).map((r) => (
              <p
                key={r.block_id}
                className="text-[11px] text-[#1F2937]"
                data-testid={`wizard-rescue-${r.student_id}`}
              >
                <span className="font-semibold">{r.student_name}</span>
                <span className="text-[#4B5563]">{" · "}</span>
                <span className="italic">
                  joined {(r.joined_students || []).join(", ")}
                </span>
                <span className="text-[#6B7280]">
                  {" · "}
                  {DAY_LABELS[r.day_of_week] || `Day ${r.day_of_week}`}
                  {" "}
                  {fmtTime(r.start_time)}–{fmtTime(r.end_time)}
                </span>
              </p>
            ))}
            {rescues.length > 10 && (
              <p className="text-[10px] text-[#6B7280] italic">
                + {rescues.length - 10} more
              </p>
            )}
          </div>
        </div>
      )}

      {summary.unscheduled_block_count === 0 && unscheduled.length === 0 && (
        <div className="border border-[#D9E5DC] rounded-lg bg-[#F2F8F4] p-3 flex items-center gap-2">
          <CheckCircle size={14} weight="fill" className="text-[#1E3F33]" />
          <p className="text-[11px] text-[#1E3F33]">
            All required blocks placed. Send to Builder to review and Save.
          </p>
        </div>
      )}

      {groupKeys.size > 0 && (
        <p className="text-[10px] text-[#6B7280] flex items-center gap-1.5">
          <UsersThree size={12} className="text-[#1E3F33]" />
          Grouped meetings stay as individual blocks per student in the Builder, sharing the same time slot (per-student invariant).
        </p>
      )}
    </div>
  );
}

function SummaryCard({ label, value, subtitle, warn }) {
  return (
    <div className="border border-[#E5E3DB] rounded-lg p-2.5 bg-white">
      <p className="text-[9px] uppercase tracking-wider text-[#6B7280]">{label}</p>
      <p
        className={`text-base font-semibold heading-font tabular-nums ${
          warn ? "text-[#B85C5C]" : "text-[#1E3F33]"
        }`}
      >
        {value}
      </p>
      <p className="text-[10px] text-[#6B7280]">{subtitle}</p>
    </div>
  );
}

// ── Apply chooser overlay ─────────────────────────────────────────────────────
// Shown only when there's an existing schedule for the provider+period.
// User chooses Coordinate (append) vs Overwrite (archive + replace).
function ApplyChooser({
  existingCount,
  applyMode,
  setApplyMode,
  archivePrevious,
  setArchivePrevious,
  applying,
  onCancel,
  onConfirm,
}) {
  return (
    <div
      className="absolute inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-center justify-center p-6"
      data-testid="apply-chooser"
    >
      <div className="bg-white rounded-2xl border border-[#E5E3DB] shadow-xl max-w-md w-full p-5 space-y-4">
        <div>
          <h3 className="text-base font-semibold text-[#1F2937] heading-font">
            Apply this draft to your schedule?
          </h3>
          <p className="text-[11px] text-[#6B7280] mt-1">
            You already have {existingCount} block{existingCount !== 1 ? "s" : ""} saved for this period. Pick how the wizard's proposal should land.
          </p>
        </div>

        <div className="space-y-2">
          <label
            data-testid="apply-mode-coordinate"
            className={`flex items-start gap-3 border rounded-lg p-3 cursor-pointer transition-colors ${
              applyMode === "coordinate"
                ? "border-[#1E3F33] bg-[#F2F8F4]"
                : "border-[#E5E3DB] hover:bg-[#F9F8F6]"
            }`}
          >
            <input
              type="radio"
              checked={applyMode === "coordinate"}
              onChange={() => setApplyMode("coordinate")}
              className="mt-0.5"
            />
            <div>
              <p className="text-[12px] font-semibold text-[#1F2937]">Coordinate</p>
              <p className="text-[11px] text-[#6B7280]">
                Add the new blocks alongside your existing schedule. Nothing is removed.
              </p>
            </div>
          </label>

          <label
            data-testid="apply-mode-overwrite"
            className={`flex items-start gap-3 border rounded-lg p-3 cursor-pointer transition-colors ${
              applyMode === "overwrite"
                ? "border-[#B85C5C] bg-[#FDF2F2]"
                : "border-[#E5E3DB] hover:bg-[#F9F8F6]"
            }`}
          >
            <input
              type="radio"
              checked={applyMode === "overwrite"}
              onChange={() => setApplyMode("overwrite")}
              className="mt-0.5"
            />
            <div>
              <p className="text-[12px] font-semibold text-[#1F2937]">Overwrite</p>
              <p className="text-[11px] text-[#6B7280]">
                Replace the {existingCount} current block{existingCount !== 1 ? "s" : ""} with the wizard's proposal.
              </p>
            </div>
          </label>
        </div>

        {applyMode === "overwrite" && (
          <label
            data-testid="apply-archive-toggle"
            className="flex items-center gap-2 text-[11px] text-[#1F2937] cursor-pointer"
          >
            <input
              type="checkbox"
              checked={archivePrevious}
              onChange={(e) => setArchivePrevious(e.target.checked)}
            />
            <span>Archive previous schedule (recommended — keeps a copy you can review later)</span>
          </label>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onCancel}
            disabled={applying}
            data-testid="apply-cancel"
            className="rounded-lg h-8 text-xs"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={onConfirm}
            disabled={applying}
            data-testid="apply-confirm"
            className={`rounded-lg h-8 text-xs ${
              applyMode === "overwrite"
                ? "bg-[#B85C5C] hover:bg-[#a04f4f] text-white"
                : "bg-[#1E3F33] hover:bg-[#173127] text-white"
            }`}
          >
            <CheckCircle size={12} weight="fill" className="mr-1" />
            {applying ? "Applying..." : applyMode === "overwrite" ? "Overwrite schedule" : "Coordinate"}
          </Button>
        </div>
      </div>
    </div>
  );
}
